import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I get consistent roofing leads in Colorado?",
    a: "Build a foundation of local SEO and Google Maps so you're the trusted local roofer Google ranks year-round — not just after a storm. Layer in retargeting ads and content that speaks to insurance claims, hail, and re-roofs to keep the pipeline full between storm cycles.",
  },
  {
    q: "What's the best marketing for roofers after a Colorado hailstorm?",
    a: "Post-storm, geo-targeted Google Ads + Local Service Ads convert fastest because intent is highest. Backing that with strong organic rankings and a Google Business Profile full of recent jobs and reviews is what separates real local roofers from out-of-state storm chasers.",
  },
  {
    q: "How do I rank on Google for roofing in Colorado Springs and Denver?",
    a: "City-specific service pages, real local citations, a fully optimized Google Business Profile per location, and consistent on-page SEO targeting 'roof repair', 'hail damage roof', and 'roof replacement' searches. Both markets are competitive — durable rankings take 4–6 months of consistent work.",
  },
  {
    q: "Should I use SEO or PPC for my roofing company?",
    a: "Both — but in the right order. PPC for immediate storm-season leads, SEO for the long-term foundation that lowers your cost per lead every year. Most of our roofing clients run both because relying on ads alone gets expensive fast.",
  },
  {
    q: "How do I beat storm chaser companies in local search results?",
    a: "Storm chasers can't fake local proof. A strong Google Business Profile with real Colorado photos, a steady review velocity from local customers, an in-state phone number, and city-specific content all outrank fly-in companies in Google's local algorithm.",
  },
];

export const Route = createFileRoute("/roofing-seo-colorado")({
  // SEO: consolidate ranking signal onto the /roofing-marketing-colorado flagship hub.
  head: () => industryMarketingHead("Roofing", "roofing", "roofing-seo-colorado", FAQS, "https://coloradowebimpressions.com/roofing-marketing-colorado"),
  component: () => (
    <IndustryMarketingPage
      industry="Roofing"
      industryLower="roofing"
      slug="roofing-seo-colorado"
      subheadline="Storm season or slow season — we keep Colorado roofers' phones ringing."
      painPoints={[
        "Storm chasers flood the market after hail — making it hard to stand out",
        "Seasonal revenue swings with no predictable off-season lead source",
        "High cost-per-click on Google Ads eating into roofing margins",
      ]}
      serviceDescriptions={{
        seo: "Rank for 'roof repair', 'hail damage', and 'roof replacement' in every Colorado city you serve.",
        googleMaps: "Dominate the Map Pack so storm-season searches go to a local roofer — you.",
        webDesign: "Roofing sites that capture insurance leads and convert mobile traffic fast.",
        ppc: "Geo-targeted ads tuned to storm zones, neighborhoods, and high-intent keywords.",
        geoAi: "Get recommended by ChatGPT and Gemini when Coloradans ask 'best roofer near me.'",
        socialMedia: "Storm-proof your brand with local content, jobsite photos, and reviews.",
      }}
      testimonial={{
        quote:
          "Other agencies talked. They actually delivered. 12x return on our Google Ads in the first quarter.",
        role: "Roofing Company Founder",
        city: "Denver",
      }}
      faqs={FAQS}
      industrySpokes={[
        {
          "to": "/roofing-google-maps-colorado",
          "label": "Roofing Google Maps in Colorado",
          "description": "Map Pack ranking for Colorado roofers — storm-ready GBP and review velocity."
        },
        {
          "to": "/roofing-ppc-colorado",
          "label": "Roofing PPC in Colorado",
          "description": "Storm-season Google Ads + LSAs built to capture insurance-claim leads."
        },
        {
          "to": "/roofing-web-design-colorado",
          "label": "Roofing web design in Colorado",
          "description": "Hand-coded roofing sites built for hail-damage traffic spikes."
        }
      ]}
      cityHubLinks={[
        {
          "to": "/colorado-springs",
          "label": "Roofing marketing in Colorado Springs",
          "description": "Springs roofing SEO and Maps — tuned for our hail-prone home market."
        },
        {
          "to": "/denver-digital-marketing",
          "label": "Roofing marketing in Denver",
          "description": "Compete in Colorado's largest roofing market with local-first SEO."
        },
        {
          "to": "/fort-collins-digital-marketing",
          "label": "Roofing marketing in Fort Collins",
          "description": "Northern Colorado storm-season roofing SEO and Maps."
        }
      ]}
    />
  ),
});
